import { Field, Formik } from "formik";
import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import Hero from "../../common/Hero/Hero";
import "./CustomSignUp.scss";
import { ADMIN_SIGN_UP, SIGN_UP, UPDATE_CUSTOMER } from "./mutations";

import { CompanyRegistrationForm } from "./CompanyRegistrationForm/CompanyRegistrationForm";
import { CondominiumAdminRegistrationForm } from "./CondominiumAdminRegistrationForm/CondominiumAdminRegistrationForm";
import { MunicipalUserRegistrationForm } from "./MunicipalUserRegistrationForm/MunicipalUserRegistrationForm";
import { PrivateRegistrationForm } from "./PrivateRegistrationForm/PrivateRegistrationForm";

import { useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { UserRoleEnum } from "../../../helpers/enums";
import { SwitchUser, TranslatedText } from "../../../helpers/helpers";
import { getEvictionForMutation } from "../../../helpers/mutations";
import { RootState } from "../../../rootReducer";
import Modal from "../../common/Modal/Modal";

const CustomSignUp = (props: any) => {
  const user = useSelector((state: RootState) => state.auth);

  const userProfile = SwitchUser(user);
  const isLegacyUser = userProfile ? userProfile.isLegacy : false;
  const { t } = props;
  const action = userProfile ? "update" : "create";
  const [userType, setUserType] = useState(userProfile ? userProfile.role : "");
  const [submitted, setSubmitted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messageConfirm, setMessageConfirm] = useState("");
  const [openPopupAdmin, setOpenPopupAdmin] = useState(false);

  const UserTypeSelector = () => {
    const initialValues = {
      role: null,
    };
    const schemaValidation = Yup.object().shape({
      name: Yup.string().required(
        (<TranslatedText i18nKey="error.signup.name.required" />) as any
      ),
    });

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={schemaValidation}
        key={"typeForm"}
        onSubmit={() => {}}
      >
        {() => (
          <div className={"row"}>
            <p
              className={
                "col-sm-12 col-md-10 offset-md-1 case-step-mandatory-fields-legend paragraph-small-fs9"
              }
            >
              {t("mandatory.fields.legend")}
            </p>
            <div className="col-sm-12 col-md-10 offset-md-1 mb-5 mt-5">
              <h2>{t("registration.header1")}</h2>
            </div>
            <div className="col-sm-12 col-md-10 offset-md-1">
              <div className="row">
                <div className={"col-lg-4 col-md-12"}>
                  <div className="form-check abc-radio">
                    <Field
                      className="form-check-input"
                      type="radio"
                      name="typeUser"
                      id="type-user"
                      value="PRIVATE"
                      checked={userType === UserRoleEnum.PRIVATE}
                      onChange={() => setUserType(UserRoleEnum.PRIVATE)}
                      data-testid={"Private-checkbox"}
                    />
                    <label className="paragraph-small-fs9" htmlFor="type-user">
                      {t("registration.typePrivate")}
                    </label>
                  </div>
                </div>
                <div className={"col-lg-4 col-md-12"}>
                  <div className="form-check abc-radio">
                    <Field
                      className="form-check-input"
                      type="radio"
                      name="type-user-business"
                      id="type-user-business"
                      value="COMPANY"
                      checked={userType === UserRoleEnum.COMPANY}
                      onChange={() => setUserType(UserRoleEnum.COMPANY)}
                      data-testid={"Company-checkbox"}
                    />
                    <label
                      className="paragraph-small-fs9"
                      htmlFor="type-user-business"
                    >
                      {t("registration.typeUserBusiness")}
                    </label>
                  </div>
                </div>
                <div className={"col-lg-4 col-md-12"}>
                  <div className="form-check abc-radio">
                    <Field
                      className="form-check-input"
                      type="radio"
                      name="type-user-building-administrator"
                      id="type-user-building-administrator"
                      value="CONDOMINIUM_ADMIN"
                      checked={userType === UserRoleEnum.CONDOMINIUM_ADMIN}
                      onChange={() => {
                        setUserType(UserRoleEnum.CONDOMINIUM_ADMIN);
                        setOpenPopupAdmin(true);
                      }}
                      data-testid={"Condominium-admin-checkbox"}
                    />
                    <label
                      className="paragraph-small-fs9"
                      htmlFor="type-user-building-administrator"
                    >
                      {t("registration.typeAdminBuilding")}
                    </label>
                  </div>
                </div>
              </div>
              {!userType && submitted && (
                <div className="row">
                  <div className="col">
                    <div className="invalid-feedback">
                      {t("registration.placeholder")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Formik>
    );
  };

  const ConfirmPopup = (props: any) => {
    return (
      props.isOpen && (
        <Modal
          show={props.isOpen}
          icon={"gc-icon-functional-check-2-col"}
          onClose={() => setIsOpen(false)}
          description={t(`common.${props.messageConfirm}`)}
          type={"link"}
          styleType={"primary"}
          to={"/"}
          linkTitle={
            props.action === "create"
              ? t("common.goToLogin")
              : t("common.goToDashboard")
          }
        />
      )
    );
  };

  const showTypedForm = (theMutation: any) => {
    const mutation = (...params: any) => {
      if (userType !== "") {
        return theMutation(...params);
      } else {
        console.log("skipping mutation since no userType selected");
      }
    };
    type FormKeys = keyof typeof forms;
    const forms = {
      [UserRoleEnum.PRIVATE]: (
        <PrivateRegistrationForm
          onSubmit={mutation}
          t={t}
          setSubmitted={setSubmitted}
          initialValues={userProfile ? userProfile : null}
          setIsOpen={setIsOpen}
          setMessageConfirm={setMessageConfirm}
          isLegacy={isLegacyUser}
        />
      ),

      [UserRoleEnum.COMPANY]: (
        <CompanyRegistrationForm
          onSubmit={mutation}
          t={t}
          setSubmitted={setSubmitted}
          initialValues={userProfile ? userProfile : null}
          setIsOpen={setIsOpen}
          setMessageConfirm={setMessageConfirm}
          isLegacy={isLegacyUser}
          data-testid={"company-form"}
        />
      ),

      [UserRoleEnum.CONDOMINIUM_ADMIN]: (
        <CondominiumAdminRegistrationForm
          onSubmit={mutation}
          t={t}
          setSubmitted={setSubmitted}
          initialValues={userProfile ? userProfile : null}
          setIsOpen={setIsOpen}
          setMessageConfirm={setMessageConfirm}
          isLegacy={isLegacyUser}
          data-testid={"condominium-form"}
        />
      ),

      [UserRoleEnum.MUNICIPAL_USER]: (
        <MunicipalUserRegistrationForm
          onSubmit={mutation}
          t={t}
          setSubmitted={setSubmitted}
          initialValues={userProfile ? userProfile : null}
          setIsOpen={setIsOpen}
          setMessageConfirm={setMessageConfirm}
          isLegacy={isLegacyUser}
        />
      ),

      [UserRoleEnum.MANAGER]: (
        <MunicipalUserRegistrationForm
          onSubmit={mutation}
          t={t}
          setSubmitted={setSubmitted}
          initialValues={null}
          setIsOpen={setIsOpen}
          setMessageConfirm={setMessageConfirm}
          isLegacy={isLegacyUser}
        />
      ),

      [UserRoleEnum.OPERATOR]: (
        <MunicipalUserRegistrationForm
          onSubmit={mutation}
          t={t}
          setSubmitted={setSubmitted}
          initialValues={null}
          setIsOpen={setIsOpen}
          setMessageConfirm={setMessageConfirm}
          isLegacy={isLegacyUser}
        />
      ),
    };
    return forms[
      (userType !== "" ? userType : UserRoleEnum.PRIVATE) as FormKeys
    ];
  };
  const [signUp] = useMutation(SIGN_UP, {
    update: (cache) => {
      const fieldsToEvict = getEvictionForMutation("signUp");
      fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
      cache.gc();
    },
  });

  const updateMutationDoc =
    userType === UserRoleEnum.OPERATOR || userType === UserRoleEnum.MANAGER
      ? ADMIN_SIGN_UP
      : UPDATE_CUSTOMER;

  const mutationNameString =
    userType === UserRoleEnum.OPERATOR || userType === UserRoleEnum.MANAGER
      ? "adminSignUp"
      : "updateCustomer";

  const [updateCustomer] = useMutation(updateMutationDoc, {
    update: (cache) => {
      const fieldsToEvict = getEvictionForMutation(mutationNameString);
      fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
      cache.gc();
    },
  });

  return (
    <div className="CustomSignUp mb-5">
      {action === "create" ? (
        <div className="container">
          <UserTypeSelector />
          {showTypedForm(signUp)}
        </div>
      ) : (
        <div className="container">{showTypedForm(updateCustomer)}</div>
      )}
      <ConfirmPopup
        isOpen={isOpen}
        messageConfirm={messageConfirm}
        action={action}
      />
      {action === "update" &&
        userType === UserRoleEnum.CONDOMINIUM_ADMIN &&
        userProfile?.isCondominiumAdminDisabled && (
          <Hero
            helpText={t(
              "case.modifica_dati.condominium_admin.disabled.profile"
            )}
            backTo={"/"}
            backToText={t("common.back.home")}
            autoOpen={true}
            block={true}
          />
        )}
      <Modal
        show={openPopupAdmin}
        onClose={() => setOpenPopupAdmin(false)}
        description={t("registration.typeAdmin.modal")}
        type={"button"}
        styleType={"primary"}
        onFirstClick={() => setOpenPopupAdmin(false)}
        linkTitle={t("common.hoCapito")}
      />
    </div>
  );
};

export default withTranslation("translation")(CustomSignUp);
